import React from 'react';
import { Link } from 'react-router-dom';
import { MonoKicker, GoldRule, Section, SectionMarker, Reveal, RevealHairline } from '../index';

/**
 * §04 THE WHERE AND THE WHEN — the trust moat.
 *
 * Every other section is loud. This one is nearly empty. An app that reads
 * your photo library asking for trust should not perform: state one sentence,
 * back it with the mechanic, stop talking. It is the only section with no
 * screenshot, and that absence is deliberate — nothing to look at means
 * nothing to hide behind. It also absorbs how-it-works, so there is no
 * standalone three-step icon strip anywhere on the page.
 *
 * ⚠️ FOUR CLAIMS HERE WERE FALSE AS FIRST DRAFTED and were corrected against
 * a full read of the frozen legal pages. Recorded so they are not reverted:
 *
 *   "We never sell or share your location data"  →  "We never sell..."
 *       Privacy §5 explicitly reserves SHARING with processors, business
 *       transfers and legal requirements. The sell half is bulletproof; the
 *       share half is contradicted by our own policy.
 *   "We never see anything you don't connect"    →  the ML-training line
 *       The original was unverifiable; the replacement is policy-verbatim.
 *   "NOTHING IS UPLOADED"                        →  "YOUR PHOTOS STAY PUT"
 *       False. Both legal docs state coordinates, timestamps and filename
 *       references ARE sent to servers for comparison.
 *   "WHAT LEAVES YOUR PHONE ···· NOTHING"        →  the two-row split below
 *       Same falsity.
 *
 * The section got STRONGER, not weaker. "Nothing is uploaded" is the kind of
 * blanket a sceptic disbelieves on sight. Naming exactly what leaves — a
 * coordinate, a timestamp, a file reference — and exactly what never does —
 * the photographs — is more persuasive because it is checkable.
 *
 * Standing rule for anyone editing this section: MARKETING YIELDS TO POLICY,
 * every time. Want a stronger claim? Amend the Privacy Policy first and cite
 * the section. Do not write the claim and hope.
 *
 * No CTA here. Trust is not a moment to sell.
 */

const BEATS = [
  {
    n: '01',
    label: 'GRANT PHOTO ACCESS',
    body: 'You choose which photos CrossedPaths can look at.',
  },
  {
    n: '02',
    label: 'WE READ THE LOCATION DATA',
    body: 'Every photo already carries a coordinate and a timestamp.',
  },
  {
    n: '03',
    label: 'YOUR PHOTOS STAY PUT',
    body: 'Only the coordinate, the timestamp and a file reference are compared. The photographs themselves never leave.',
  },
];

/**
 * Every line here maps to an exact line in the frozen legal text.
 *
 * Item 2's second sentence is privacy.html:95 near-verbatim, and it is better
 * copy than anything we would have written: three escalating negations
 * landing on an absolute. It renders in --cp-text-2 so it reads as the sworn
 * detail under the claim rather than as a second claim.
 *
 * The unscoped verb "share" must NEVER appear here. privacy.html:106–112
 * reserves five sharing channels — your connection, service providers,
 * business transfers, legal requirements, consent. Even "never share without
 * your consent" fails, because business transfers and legal requirements are
 * not consent-gated. The "sell" half is categorical and unhedged, so that is
 * what we claim.
 */
const NEVER = [
  { text: 'We never upload your photos.' },
  {
    text: 'We never sell your location data.',
    detail: 'Not raw, not aggregated, not de-identified — not to anyone.',
  },
  { text: 'We never use your data to train machine-learning models.' },
];

export function Privacy() {
  return (
    <Section
      id="privacy-promise"
      labelledBy="privacy-h"
      tone="void"
      pad={false}
      className="py-[clamp(120px,16vw,220px)]"
    >
      <SectionMarker className="mb-10">§ 04 · Chain of custody</SectionMarker>

      <Reveal>
        <GoldRule />
        <MonoKicker className="mt-2">ON-DEVICE · BY DESIGN</MonoKicker>
      </Reveal>

      {/* ---- the claim: the only display-scale serif on the site ----
          Poster type would make it shout; the serif makes it certain.
          Ships unqualified — no "designed to", no hedging adverb, no glow.
          It does not need help. */}
      <Reveal delay={100} duration={800} className="mt-10 lg:max-w-[66%]">
        <h2
          id="privacy-h"
          data-testid="privacy-claim"
          className="max-w-[18ch] font-serif text-[length:clamp(1.875rem,1.1rem+3.9vw,3.25rem)] leading-[1.22] tracking-[-0.01em] text-paper"
        >
          Photos never leave your device.{' '}
          <em className="italic text-gold-400">We only read the where and the when.</em>
        </h2>
      </Reveal>

      {/* ---- the method: how-it-works, absorbed ---- */}
      <div className="mt-16" data-testid="privacy-method">
        <Reveal delay={300}>
          <MonoKicker>HOW IT WORKS</MonoKicker>
        </Reveal>

        {/* At ≥768 one continuous rule runs under all three beats. A single
            rule reads as a chain of custody; three rules read as three cards. */}
        <ol className="mt-6 md:grid md:grid-cols-3 md:gap-10">
          {BEATS.map((beat, i) => (
            <li key={beat.n}>
              <Reveal delay={400 + i * 90}>
                <p className="cp-mono text-[length:var(--fs-mono-value)] normal-case tracking-[0.18em]">
                  {beat.n}
                </p>
                <p className="cp-mono mt-2 text-[length:var(--fs-mono)] text-paper">
                  {beat.label}
                </p>
                <p className="mt-3 max-w-[42ch] text-[length:var(--fs-body)] leading-[1.6] text-ink-2 md:min-h-[6rem]">
                  {beat.body}
                </p>
              </Reveal>
              <RevealHairline delay={400 + i * 90} className="mt-4 md:hidden" />
            </li>
          ))}
        </ol>
        <RevealHairline delay={580} className="mt-4 hidden md:block" />
      </div>

      <div className="mt-16 grid gap-12 lg:grid-cols-12 lg:gap-10">
        {/* ---- what we never do ---- */}
        <Reveal delay={700} className="lg:col-span-6">
          {/* No glass, no glow, and the left border is a hairline rather than
              gold: this section's restraint is the argument, and a panel
              glowing gold undercuts the sobriety that makes the claim
              believable. */}
          <div className="rounded-[16px] border border-[color:var(--cp-hairline)] border-l-2 border-l-[color:var(--cp-hairline-strong)] bg-surface px-[22px] py-6">
            <p className="cp-mono text-[length:var(--fs-mono)] text-paper">▚ What we never do</p>
            <ul className="mt-4 space-y-3">
              {NEVER.map((item) => (
                <li key={item.text} className="flex gap-3">
                  <span aria-hidden="true" className="text-ink-2">
                    ─
                  </span>
                  <span className="text-[length:var(--fs-body)] leading-[1.6] text-paper-soft">
                    {item.text}
                    {item.detail && (
                      <>
                        {' '}
                        <span className="text-ink-2">{item.detail}</span>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* A trust signal, not a CTA — never a pill. */}
          <Link
            to="/privacy"
            className="cp-mono mt-6 inline-block text-[length:var(--fs-mono)] underline-offset-4 transition-colors duration-150 hover:text-paper hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-400"
          >
            Read the full privacy policy →
          </Link>
        </Reveal>

        {/* ---- the ledger ---- */}
        <Reveal delay={820} className="lg:col-span-5 lg:col-start-8">
          <div className="flex flex-col items-start gap-1 pb-[10px] min-[421px]:flex-row min-[421px]:items-baseline">
            <span className="cp-mono text-[length:var(--fs-mono)] whitespace-nowrap">
              Your photos
            </span>
            <span
              aria-hidden="true"
              className="mx-3 hidden min-w-[24px] flex-1 -translate-y-[0.28em] border-b border-dotted border-[color:var(--cp-stroke-muted)] min-[421px]:block"
            />
            <span className="cp-mono text-[length:var(--fs-mono-value)] text-gold-400">
              Never leave this phone
            </span>
          </div>
          <span aria-hidden="true" className="block h-px w-full bg-[color:var(--cp-hairline)]" />

          <div className="mt-3 flex flex-col items-start gap-1 pb-[10px] min-[421px]:flex-row min-[421px]:items-baseline">
            <span className="cp-mono text-[length:var(--fs-mono)] whitespace-nowrap">
              What is compared
            </span>
            <span
              aria-hidden="true"
              className="mx-3 hidden min-w-[24px] flex-1 -translate-y-[0.28em] border-b border-dotted border-[color:var(--cp-stroke-muted)] min-[421px]:block"
            />
            <span className="cp-mono text-[length:var(--fs-mono-value)] text-paper">
              Coordinate · Timestamp · File reference
            </span>
          </div>
          <span aria-hidden="true" className="block h-px w-full bg-[color:var(--cp-hairline)]" />

          <p className="cp-story mt-8 max-w-[38ch] text-[length:var(--fs-serif)] text-ink-2">
            The coldest data you own, and the warmest thing it can tell you.
          </p>
        </Reveal>
      </div>
    </Section>
  );
}

export default Privacy;
